import { BreadcrumbItems, BreadcrumbJSONLDSchema } from '@noths/polaris-client-next-components';
import { toSentenceCase } from '@noths/polaris-client-utils';
import { Link } from '@noths/polaris-dev-ts-types';
import { createSelector } from '@reduxjs/toolkit';
import { maxBy, reduce } from 'ramda';

import { ReduxApplicationState } from 'src/redux/combinedReducer';

const selectAndDecorateBreadcrumbData = (state: ReduxApplicationState) =>
  selectBreadcrumbHierarchy(state).reduce(
    (items, { href, rel, title }) =>
      rel?.toLowerCase() === '/home'
        ? items
        : items.concat({
            rel,
            href,
            title: (title && toSentenceCase(title)) ?? '',
          }),
    [] as Link[],
  );

export const selectBreadcrumbHierarchy = (state: ReduxApplicationState) =>
  state.product.navigation.find((n) => n.type === 'breadcrumb')?.options ?? [];

export const selectDefaultHierarchyLeaf = createSelector(
  selectBreadcrumbHierarchy,
  reduce(
    maxBy((link: Link) => link?.href?.length || 0),
    {} as Link,
  ),
);

export const selectBreadcrumbJSONLDSchema = createSelector(
  selectAndDecorateBreadcrumbData,
  (hierarchy): BreadcrumbJSONLDSchema => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: hierarchy.map(({ title, href }, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: title || undefined,
      item: href || undefined,
    })),
  }),
);

export const selectBreadcrumbData = createSelector(
  selectAndDecorateBreadcrumbData,
  (breadcrumbs): BreadcrumbItems =>
    breadcrumbs.map(({ rel, href, title }) => ({
      rel: rel ?? '',
      href: href ?? '',
      title: title ?? '',
    })),
);
